
import * as React from 'react';
import { openItem } from '../../operations/openItem';
import { resolveItems, supportFacets } from '../../operations/getItems';
import { injectInitialProps } from '../../utilities/components/Props';
import { Fragment } from '../../utilities/components/Fragment';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IAddon, createAddon } from '../../addons/registerOperationHandler';
import { IItemCacheProviderProps, ItemCacheProvider } from '../itemCacheProvider/ItemCacheProvider';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { mark, now } from '../../utilities/performance';
import { Omit } from '../../utilities/Omit';
import { readItems } from '../../operations/readItems';
import { IItemCache } from '../../models/IItemCacheObserver';
import { itemsScopeInitTimeFacet } from '../../facets/LoggerFacets';
import { withDisplayName } from '../../utilities/components/Decorators';
import { ControlHandlerProvider, IControlFaceted } from '../decorators/withControlHandler';
import { itemsScopeControl } from '../../controls/itemsScopeControl';
import { itemsScopeItemKeyFacet, getNextItemsScopeItemKey } from '../../models/itemsScope/getNextItemsScopeItemKey';
import { IItemCacheInitializerPayload, createItemCacheInitializer } from '../itemCacheInitializer/ItemCacheInitializer';

class ItemsScopeContext extends React.PureComponent<IItemsScopeStateProps> {
  private _controlHandlers: IControlFaceted;

  constructor(props: IItemsScopeStateProps) {
    super(props);

    this._controlHandlers = itemsScopeControl.pack(this._itemsScopeControlHandler);
  }

  public render(): JSX.Element {
    return (
      <ControlHandlerProvider
        controlHandlers={ this._controlHandlers }
      >
        <Fragment>{ this.props.children }</Fragment>
      </ControlHandlerProvider>
    );
  }

  private _itemsScopeControlHandler = (): typeof itemsScopeControl.returnType => {
    return {
      itemsScopeItemKey: this.props.itemsScopeItemKey
    };
  }
}

const itemsScopeAddon = createAddon(() => {
  return (dispatch: IItemCacheDispatch) => {
    dispatch(supportFacets({
      keyFacet: itemsScopeItemKeyFacet,
      facets: []
    }));
  };
});

/**
 * @public
 */
export { getNextItemsScopeItemKey };

/**
 * @public
 */
export interface IItemsScopeInitializerPayload {
  itemsScopeItemKey: string;
}

/**
 * @public
 */
export type IItemsScopeInitializer = (payload: IItemsScopeInitializerPayload) => IItemCacheThunkAction<void>;

/**
 * Creates an initializer to be passed to `<ItemsScope initializer={ }>` for a given
 * starting data source and key.
 * @public
 */
export function createInitializer(addon: IAddon, itemKey: string): IItemsScopeInitializer {
  return (payload: {
    itemsScopeItemKey: string;
  }) => (dispatch: IItemCacheDispatch) => {
    dispatch(addon);
    dispatch(openItem({
      itemsScopeItemKey: payload.itemsScopeItemKey,
      currentItemKey: itemKey
    }));
  };
}

function logItemsScopeInitTime(itemsScopeItemKey: string): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    mark(`ItemsScope.Init.Start (${itemsScopeItemKey})`);

    const initTime = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(itemsScopeInitTimeFacet, itemsScopeItemKey)));

    if (!initTime) {
      dispatch(resolveItems({
        items: {
          [itemsScopeItemKey]: {
            ...itemsScopeInitTimeFacet.pack(Math.round(now()))
          }
        }
      }));
    }
  };
}

interface IItemsScopeInitializerProps {
  initializer?: IItemsScopeInitializer;
  itemsScopeItemKey: string;
}

const ItemsScopeInitializer = withDisplayName('ItemsScopeInitializer')(
  connectToItemsScope()(
    createItemCacheInitializer((
      payload: IItemCacheInitializerPayload<IItemsScopeInitializerProps>
    ) => {
      return (dispatch: IItemCacheDispatch) => {
        const {
          initializer,
          itemsScopeItemKey
        } = payload.props;

        if (!payload.previousProps) {
          dispatch(itemsScopeAddon);

          dispatch(logItemsScopeInitTime(itemsScopeItemKey));

          if (initializer) {
            dispatch(initializer({
              itemsScopeItemKey: itemsScopeItemKey
            }));
          }
        }
      };
    })
  )
);

/**
 * @public
 */
export interface IItemsScopeStateProps {
  itemsScopeItemKey: string;
}

/**
 * @public
 */
export interface IItemsScopeProps extends IItemCacheProviderProps, IRenderProps<IItemsScopeStateProps> {
  /**
   * An initializer for the items scope to select the starting data source and key.
   */
  initializer?: IItemsScopeInitializer;
  itemsScopeItemKey?: string;
}

/**
 * Component which maintains a state with a current item and selection,
 * providing access to a default item cache and behaviors.
 * @public
 */
export const ItemsScope: React.ComponentType<IItemsScopeProps> =
  withDisplayName('ItemsScope')(injectInitialProps((props: Pick<IItemsScopeProps, 'itemsScopeItemKey'>) => {
    const {
      itemsScopeItemKey = getNextItemsScopeItemKey()
    } = props;

    return {
      itemsScopeItemKey: itemsScopeItemKey
    };
  })(withDisplayName('ItemsScope.Inner')((props: Omit<IItemsScopeProps, 'itemsScopeItemKey'> & {
    itemsScopeItemKey: string;
  }): JSX.Element => {
    const {
      initializer,
      itemsScopeItemKey,
      children,
      // Need to prevent the above props from propagating to the ItemCacheProvider.
      ...itemCacheProps
    } = props;

    return (
      <ItemCacheProvider { ...itemCacheProps }>
        <ItemsScopeContext itemsScopeItemKey={ itemsScopeItemKey }>
          <ItemsScopeInitializer
            initializer={ initializer }
          >
            {
              renderChildrenWithProps(children, {
                itemsScopeItemKey
              })
            }
          </ItemsScopeInitializer>
        </ItemsScopeContext>
      </ItemCacheProvider>
    );
  })));

/**
 * @public
 */
export interface IWithItemsScopeProps extends IRenderProps<IItemsScopeStateProps> {
  // Nothing additional.
}

/**
 * Render-prop component which provides access to the itemsScopeItemKey for the current ItemsScope.
 * @public
 */
export const WithItemsScope: React.ComponentType<IWithItemsScopeProps> = withDisplayName('WithItemsScope')(connectToItemsScope()(
  withDisplayName('WithItemsScope.Callback')((props: IItemsScopeStateProps & IWithItemsScopeProps) => {
    const {
      itemsScopeItemKey,
      children
    } = props;

    return renderChildrenWithProps(children, {
      itemsScopeItemKey
    });
  })));
